/**
 * ATools Shopify checkout adapter for Horizon; theme-only code.
 * Server remains authoritative for supplier offers, shipping, and Shopify draft creation.
 * No ordinary /checkout fallback. Requires ATOOLS_MULTI_CHECKOUT_ENABLED=YES server-side.
 */
(() => {
  'use strict';
  if (window.__atoolsCheckoutGlobalV1) return;
  window.__atoolsCheckoutGlobalV1 = true;
  let busy = false;

  const btnSelector = 'button[data-atools-checkout]';
  const warn = (message, error) => {
    console.warn('ATools checkout:', message, error || '');
  };
  const statusFor = (button, message) => {
    const container = button.closest('.cart__ctas');
    const node = container?.querySelector('[data-atools-checkout-status]');
    if (node) {
      node.textContent = message;
      node.hidden = !message;
    }
  };
  const setLabel = (button, label, text) => {
    if (label) label.textContent = text;
    statusFor(button, text);
  };
  const fetchJson = async (url, options, errorMessage) => {
    const response = await fetch(url, options);
    let data;
    try { data = await response.json(); }
    catch (_) { throw new Error(errorMessage + ' (HTTP ' + response.status + ').'); }
    if (!response.ok) {
      const reason = (typeof data?.detail === 'string' ? data.detail : '').slice(0, 180);
      throw new Error(errorMessage + ' (HTTP ' + response.status + ').' + (reason ? ' ' + reason : ''));
    }
    return data;
  };

  document.addEventListener('click', async (event) => {
    const button = event.target instanceof Element ? event.target.closest(btnSelector) : null;
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy || button.disabled) return;

    busy = true;
    button.disabled = true;
    const label = button.querySelector('.button-text');
    const previous = label?.textContent ?? '';
    let createStarted = false;
    let completed = false;
    setLabel(button, label, 'ATools tikrina krepšelį…');

    try {
      const cart = await fetchJson('/cart.js', {
        credentials: 'same-origin', cache: 'no-store'
      }, 'Nepavyko nuskaityti krepšelio');
      if (!Array.isArray(cart.items) || cart.items.length < 1 || cart.items.length > 12) {
        throw new Error('Krepšelyje turi būti nuo 1 iki 12 prekių eilučių.');
      }
      if (cart.currency !== 'EUR') throw new Error('Šiuo metu palaikoma tik EUR valiuta.');
      if (Number(cart.total_discount || 0) !== 0) {
        throw new Error('Krepšelio nuolaidos šiuo metu nepalaikomos.');
      }
      const qty = cart.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      if (qty > 40 || cart.items.some(item => !Number.isInteger(item.quantity)
          || item.quantity < 1 || item.quantity > 10)) {
        throw new Error('Viršytos krepšelio kiekio ribos.');
      }
      const token = cart.token;
      if (typeof token !== 'string' || !/^[A-Za-z0-9_?=:.-]{8,256}$/.test(token)) {
        throw new Error('Nepatvirtintas Shopify krepšelio identifikatorius.');
      }
      const body = {
        country: 'LT',
        items: cart.items.map(item => ({
          variant_id: item.variant_id,
          quantity: item.quantity,
          sku: item.sku || '',
          has_properties: !!(item.properties && Object.keys(item.properties).length),
          has_selling_plan: !!item.selling_plan_allocation,
          has_discount: !!((item.discounts && item.discounts.length) ||
            (item.line_level_discount_allocations && item.line_level_discount_allocations.length)),
          original_price: item.original_price,
          final_price: item.final_price
        }))
      };

      const preview = await fetchJson('/apps/atools-search/checkout/preview', {
        method: 'POST',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        credentials: 'same-origin', cache: 'no-store',
        body: JSON.stringify(body)
      }, 'ATools negali patvirtinti krepšelio');
      const shipping = Number(preview.shipping_eur);
      const suppliers = Number(preview.supplier_count);
      if (preview.status !== 'verified' || preview.currency !== 'EUR' ||
          preview.country !== 'LT' || !Number.isFinite(shipping) || shipping < 0 ||
          !Number.isSafeInteger(suppliers) || suppliers < 1 ||
          !/^[a-f0-9]{20}$/.test(String(preview.fingerprint || ''))) {
        throw new Error('Neteisingas ATools krepšelio patikros atsakymas.');
      }

      // Do not require a second confirmation click. Server rechecks before mutation.
      setLabel(button, label, 'Ruošiamas saugus atsiskaitymas…');
      createStarted = true;
      const result = await fetchJson('/apps/atools-search/checkout/create', {
        method: 'POST',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        credentials: 'same-origin', cache: 'no-store',
        body: JSON.stringify({...body, cart_token: token})
      }, 'ATools atsiskaitymo paruošti nepavyko');

      if (result.status !== 'checkout_ready' ||
          Number(result.shipping_eur) !== shipping ||
          typeof result.checkout_url !== 'string') {
        throw new Error('Shopify nepatvirtino atsiskaitymo sumos ar nuorodos.');
      }
      const invoice = new URL(result.checkout_url);
      const allowed = new Set(['atools.lt', 'yggagt-gf.myshopify.com', 'checkout.shopify.com']);
      if (invoice.protocol !== 'https:' || invoice.username || invoice.password ||
          (invoice.port && invoice.port !== '443') || !allowed.has(invoice.hostname) ||
          invoice.pathname === '/' || !invoice.pathname) {
        throw new Error('Shopify grąžino nepatvirtintą atsiskaitymo adresą.');
      }
      completed = true;
      setLabel(button, label, 'Atidaromas Shopify atsiskaitymas…');
      window.location.assign(invoice.href);
    } catch (error) {
      warn('Atsiskaitymas sustabdytas', error);
      const detail = error instanceof Error ? error.message : 'Nežinoma klaida.';
      const next = createStarted
        ? 'Juodraštis galėjo būti sukurtas. Nekartokite, susisiekite su ATools.'
        : 'Juodraštis nekurtas. Pabandykite dar kartą arba susisiekite su ATools.';
      statusFor(button, 'Atsiskaitymas sustabdytas. ' + next);
      alert('ATools: ' + detail + '\n' + next + '\nStandartinis Shopify atsiskaitymas neatidarytas.');
    } finally {
      if (!createStarted && !completed) {
        button.disabled = false;
        if (label) label.textContent = previous;
      }
      // After create POST, including uncertain errors, block a second click until reload.
      busy = false;
    }
  }, true);
})();
